import React from "react"
import ReactDOM from "react-dom"

const TeamSelector = ({ teams, joinedTeamIds = [], onSelect, onClose }) => {
  if (!teams || teams.length === 0) return null

  const getRoleCount = (players, role) => {
    if (!players) return 0;
    return players.filter(p => {
        const r = (p.role || "").toLowerCase();
        if (role === 'WK') return r.includes("wk") || r.includes("wicket")
        if (role === 'BAT') return r.includes("bat")
        if (role === 'AR') return r.includes("all-round") || r.includes("ar")
        if (role === 'BOWL') return r.includes("bowl")
        return false;
    }).length;
  }

  // ✅ USE PORTAL TO BREAK OUT OF PARENT TRANSFORM CONSTRAINTS
  return ReactDOM.createPortal(
    <div 
        className="fixed inset-0 z-[9999] flex flex-col justify-end bg-theme-bg/80 backdrop-blur-sm" 
        style={{ animation: 'fadeInTeamSelector 0.4s ease-out' }}
        onClick={onClose}
    >
      <div 
        className="bg-theme-card w-full max-w-md mx-auto rounded-t-[3rem] shadow-2xl border-t-2 border-orange-500/30 p-8 overflow-hidden relative"
        style={{ animation: 'slideUpTeamSelector 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Background Glows */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-orange-500/10 blur-[80px] pointer-events-none rounded-full" />
        <div className="absolute top-1/2 left-0 w-32 h-32 bg-green-500/5 blur-[60px] pointer-events-none rounded-full" />

        {/* Header */}
        <div className="flex justify-between items-center mb-8 px-2 relative z-10">
          <div>
            <h3 className="text-3xl font-black text-theme-text uppercase italic tracking-tighter bg-gradient-to-r from-white via-gray-400 to-gray-600 bg-clip-text text-transparent drop-shadow-sm leading-none">
              Select Team
            </h3>
            <div className="flex items-center gap-2 mt-2">
               <span className="h-1 w-8 bg-orange-500 rounded-full"></span>
               <p className="text-[10px] text-theme-text-sec font-black uppercase tracking-[0.2em]">Choose your squad</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="w-12 h-12 rounded-2xl bg-theme-bg border border-theme-border flex items-center justify-center text-theme-text/50 hover:text-theme-text transition-all transform active:scale-90 hover:bg-theme-card hover:border-theme-border"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        {/* Teams List */}
        <div className="space-y-6 max-h-[60vh] overflow-y-auto pb-10 pr-1 relative z-10 custom-scrollbar-orange">
          {teams.map((team, idx) => {
            const players = team.players || [];
            const cap = players.find(p => p.is_captain);
            const vcap = players.find(p => p.is_vice_captain);
            
            const wkCount = getRoleCount(players, 'WK');
            const batCount = getRoleCount(players, 'BAT');
            const arCount = getRoleCount(players, 'AR');
            const bowlCount = getRoleCount(players, 'BOWL');

            const isJoined = Array.isArray(joinedTeamIds) && joinedTeamIds.includes(team.team_id || team.id)

            return (
              <div 
                key={team.team_id || team.id}
                onClick={() => !isJoined && onSelect(team.team_id || team.id)}
                className={`group relative rounded-3xl p-5 border transition-all shadow-theme-md ${isJoined ? 'bg-theme-card/50 border-theme-border opacity-60 cursor-not-allowed' : 'bg-theme-card border-theme-border hover:border-orange-500/50 cursor-pointer hover:shadow-orange-500/10 active:scale-[0.98]'}`}
              >
                {/* Visual Accent */}
                {!isJoined && <div className="absolute left-0 top-6 bottom-6 w-1 bg-gradient-to-b from-orange-500 to-red-600 rounded-r-full opacity-0 group-hover:opacity-100 transition-opacity" />}

                {/* Team Info Header */}
                <div className="flex justify-between items-center mb-5">
                   <div className="flex flex-col">
                      <span className="text-[10px] font-black uppercase tracking-[0.3em] text-theme-text-sec group-hover:text-orange-500 transition-colors">Team {idx + 1}</span>
                      <h4 className="text-sm font-black text-theme-text uppercase italic mt-1 tracking-wider opacity-80 group-hover:opacity-100 transition-opacity">SQUAD READY</h4>
                   </div>
                   <div className="flex gap-2">
                       {/* Captain Badge */}
                       <div className="flex items-center gap-2 bg-theme-bg/60 p-1.5 pr-4 rounded-xl border border-yellow-500/20 shadow-lg">
                          <div className="w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center text-black font-black text-[10px] shadow-lg shadow-yellow-500/20">C</div>
                          <span className="text-[10px] font-black text-theme-text/90 truncate max-w-[70px] uppercase tracking-tight">{cap?.player_name?.split(' ')[0] || "CAPT"}</span>
                       </div>
                       {/* VC Badge */}
                       <div className="flex items-center gap-2 bg-theme-bg/60 p-1.5 pr-4 rounded-xl border border-blue-500/20 shadow-lg">
                          <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-theme-text font-black text-[10px] shadow-lg shadow-blue-500/20">VC</div>
                          <span className="text-[10px] font-black text-theme-text/90 truncate max-w-[70px] uppercase tracking-tight">{vcap?.player_name?.split(' ')[0] || "VC"}</span>
                       </div>
                   </div>
                </div>

                {/* Team Composition Info */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                   {[
                     { label: 'WK', count: wkCount },
                     { label: 'BAT', count: batCount },
                     { label: 'AR', count: arCount },
                     { label: 'BOWL', count: bowlCount }
                   ].map((role) => (
                      <div key={role.label} className="bg-theme-bg/30 p-2 rounded-2xl border border-theme-border/20 flex flex-col items-center">
                         <span className="text-[8px] font-black text-theme-text-sec uppercase tracking-widest leading-none mb-1">{role.label}</span>
                         <span className="text-sm font-black text-theme-text leading-none">{role.count}</span>
                      </div>
                   ))}
                </div>

                {/* ACTION BUTTON */}
                <div className={`w-full py-2.5 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg ${isJoined ? 'bg-theme-card border border-theme-border' : 'bg-gradient-to-r from-orange-600 to-orange-400 group-hover:from-green-600 group-hover:to-green-400 group-hover:shadow-green-500/20'}`}>
                   <span className="text-theme-text font-black text-[11px] uppercase tracking-[0.2em] italic">{isJoined ? 'ALREADY JOINED' : 'Join With This Team'}</span>
                   {isJoined ? (
                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                   ) : (
                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="animate-pulse"><polyline points="9 18 15 12 9 6"></polyline></svg>
                   )}
                </div>
              </div>
            )
          })}
        </div>
        
        {/* Safe Area Padding */}
        <div className="h-6" />
      </div>
      
      {/* STABLE CUSTOM ANIMATIONS */}
      <style>{`
        @keyframes fadeInTeamSelector {
          from { opacity: 0; background-color: rgba(0,0,0,0); backdrop-filter: blur(0px); }
          to { opacity: 1; background-color: rgba(0,0,0,0.8); backdrop-filter: blur(8px); }
        }
        @keyframes slideUpTeamSelector {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .custom-scrollbar-orange::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar-orange::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar-orange::-webkit-scrollbar-thumb { background: rgba(249, 115, 22, 0.2); border-radius: 10px; }
        .custom-scrollbar-orange::-webkit-scrollbar-thumb:hover { background: rgba(249, 115, 22, 0.5); }
      `}</style>
    </div>,
    document.body
  )
}

export default TeamSelector
